import { useEffect, useState } from 'react';
import jobsData from '../../data/jobs.json';
import { API_ENDPOINTS } from '../../config/constants';
import './JobsOpenRolesSection.css';
import ScanMyFitDrawer from './ScanMyFitDrawer';

const fallbackJobs = jobsData.jobs || [];
const browseRoles = jobsData.browseByRole || [];
const browseByRoleCount = browseRoles.length || jobsData.counts?.browseByRole || 0;
const focusTrack = jobsData.focusTrack || 'Copilot / Power Platform';
const updatedLabel = jobsData.updatedLabel || 'Updated weekly · hand-curated';

function buildInitials(...parts) {
  const text = parts
    .filter(Boolean)
    .join(' ')
    .trim();

  const initials = text
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase();

  return initials || 'LWT';
}

function formatDateLabel(value) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return 'Open';
  }
  return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

function formatUpdatedAgo(value) {
  const createdAt = new Date(value);
  if (Number.isNaN(createdAt.getTime())) {
    return 'Recently posted';
  }

  const days = Math.max(0, Math.floor((Date.now() - createdAt.getTime()) / (1000 * 60 * 60 * 24)));
  if (days === 0) return 'Today';
  if (days === 1) return '1 day ago';
  return `${days} days ago`;
}

function formatSkillsLabel(value) {
  const items = String(value || '')
    .split(/[;,]/)
    .map((item) => item.trim())
    .filter(Boolean);

  if (!items.length) {
    return 'General skills';
  }

  return items.join(' · ');
}

function mapApiJobToCard(job) {
  const title = String(job?.title || '').trim() || 'Untitled Role';
  const company = String(job?.company || '').trim() || 'Lead With Tech';
  const location = String(job?.location || '').trim() || 'India';
  const primaryskills = formatSkillsLabel(job?.primaryskills);
  const secondskills = formatSkillsLabel(job?.secondskills);

  return {
    id: job?.id,
    initials: buildInitials(title, company),
    title,
    company,
    location,
    mode: primaryskills,
    salary: `Apply by ${formatDateLabel(job?.applyby)}`,
    benchmarkTrack: 'LWT Jobs',
    benchmarkRole: secondskills,
    updatedAgo: formatUpdatedAgo(job?.createdon),
  };
}

function JobsOpenRolesSection({ onApplySuccess }) {
  const [activeView, setActiveView] = useState('open');
  const [openJobsView, setOpenJobsView] = useState('list');
  const [searchTerm, setSearchTerm] = useState('');
  const [drawerJob, setDrawerJob] = useState(null);
  const [openJobs, setOpenJobs] = useState(fallbackJobs);
  const normalizedSearch = searchTerm.trim().toLowerCase();
  const filteredOpenJobs = normalizedSearch
    ? openJobs.filter((job) => [
      job.title,
      job.company,
      job.location,
      job.mode,
      job.benchmarkRole,
      job.benchmarkTrack,
    ].some((field) => String(field || '').toLowerCase().includes(normalizedSearch)))
    : openJobs;
  const filteredBrowseRoles = normalizedSearch
    ? browseRoles.filter((role) => [
      role.title,
      role.track,
      role.level,
      role.mode,
      role.salary,
      ...(Array.isArray(role.keySkills) ? role.keySkills : []),
    ].some((field) => String(field || '').toLowerCase().includes(normalizedSearch)))
    : browseRoles;
  const openJobsCount = openJobs.length;
  const jobCount = activeView === 'roles' ? filteredBrowseRoles.length : filteredOpenJobs.length;

  useEffect(() => {
    let isMounted = true;

    const loadOpenJobs = async () => {
      try {
        const response = await fetch(API_ENDPOINTS.LWT_JOBS);
        const data = await response.json();

        if (!response.ok) {
          throw new Error(data?.error || `Failed to fetch jobs with status ${response.status}`);
        }

        if (!Array.isArray(data)) {
          throw new Error('Jobs API returned an invalid response');
        }

        const mappedJobs = data.map(mapApiJobToCard);
        if (isMounted) {
          setOpenJobs(mappedJobs);
        }
      } catch (error) {
        console.error('Unable to load open jobs from API:', error.message);
      }
    };

    loadOpenJobs();

    return () => {
      isMounted = false;
    };
  }, []);

  const openRoleFitDrawer = (role) => {
    setDrawerJob({
      id: role.id,
      applicationJobId: 9999,
      initials: role.title
        .split(' ')
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part[0])
        .join('')
        .toUpperCase(),
      title: role.title,
      company: 'Target role',
      location: role.mode,
      mode: role.level,
      salary: role.salary,
      benchmarkTrack: role.track,
      benchmarkRole: role.title,
      updatedAgo: 'Role benchmark',
    });
  };

  return (
    <section className="jobs-open-roles" aria-labelledby="jobs-open-roles-title">
      <div className="jobs-open-roles__inner">
        <div className="jobs-open-roles__switch" role="tablist" aria-label="Jobs board view">
          <button
            type="button"
            role="tab"
            className={`jobs-open-roles__switch-item ${activeView === 'roles' ? 'is-active' : ''}`}
            aria-selected={activeView === 'roles'}
            onClick={() => setActiveView('roles')}
          >
            Browse by role
            <span>{browseByRoleCount}</span>
          </button>
          <button
            type="button"
            role="tab"
            className={`jobs-open-roles__switch-item ${activeView === 'open' ? 'is-active' : ''}`}
            aria-selected={activeView === 'open'}
            onClick={() => setActiveView('open')}
          >
            Open jobs
            <span>{openJobsCount}</span>
          </button>
        </div>

        <div className="jobs-open-roles__meta-row">
          <h2 id="jobs-open-roles-title">
            {activeView === 'roles'
              ? `${jobCount} roles to explore`
              : `${jobCount} open jobs in ${focusTrack}`}
          </h2>
          <div className="jobs-open-roles__meta-actions">
            <p>{updatedLabel}</p>
            {activeView === 'open' && (
              <div className="jobs-open-roles__view-toggle" role="group" aria-label="Open jobs layout">
                <button
                  type="button"
                  className={`jobs-open-roles__view-btn ${openJobsView === 'list' ? 'is-active' : ''}`}
                  aria-pressed={openJobsView === 'list'}
                  onClick={() => setOpenJobsView('list')}
                >
                  List view
                </button>
                <button
                  type="button"
                  className={`jobs-open-roles__view-btn ${openJobsView === 'grid' ? 'is-active' : ''}`}
                  aria-pressed={openJobsView === 'grid'}
                  onClick={() => setOpenJobsView('grid')}
                >
                  Grid view
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="jobs-open-roles__search-wrap">
          <span className="jobs-open-roles__search-icon" aria-hidden="true">⌕</span>
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="jobs-open-roles__search"
            placeholder={activeView === 'roles'
              ? 'Search displayed roles by title, track, level, or skills...'
              : 'Search displayed jobs by title, company, location, or skills...'}
            aria-label={activeView === 'roles' ? 'Search displayed roles' : 'Search displayed jobs'}
          />
        </div>

        {activeView === 'roles' ? (
          <div className="jobs-open-roles__grid jobs-open-roles__grid--roles">
            {filteredBrowseRoles.map((role) => (
              <article className="role-browse-card" key={role.id}>
                <div className="role-browse-card__head">
                  <p className="role-browse-card__eyebrow">Role benchmark</p>
                  <h3>{role.title}</h3>
                  <p>{role.track}</p>
                </div>

                <div className="role-browse-card__tags" aria-label="Role metadata">
                  <span>{role.level}</span>
                  <span>{role.mode}</span>
                  <span className="is-salary">{role.salary}</span>
                </div>

                <p className="role-browse-card__skills">Key skills: {role.keySkills.join(' · ')}</p>

                <div className="role-browse-card__footer">
                  <button type="button" className="job-role-card__btn job-role-card__btn--outline">
                    View roadmap
                  </button>
                  <button
                    type="button"
                    className="job-role-card__btn job-role-card__btn--primary"
                    onClick={() => openRoleFitDrawer(role)}
                  >
                    Find my fit →
                  </button>
                </div>
              </article>
            ))}
          </div>
        ) : (
          <div className={`jobs-open-roles__grid ${openJobsView === 'list' ? 'jobs-open-roles__grid--list' : ''}`}>
            {filteredOpenJobs.map((job) => (
              <article className={`job-role-card ${openJobsView === 'list' ? 'job-role-card--list' : ''}`} key={job.id}>
                <div className="job-role-card__head">
                  <div className="job-role-card__avatar" aria-hidden="true">{job.initials}</div>
                  <div>
                    <h3>{job.title}</h3>
                    <p>{job.company}</p>
                  </div>
                </div>

                <div className="job-role-card__tags" aria-label="Role metadata">
                  <span>{job.location}</span>
                  <span>{job.mode}</span>
                  <span className="is-salary">{job.salary}</span>
                </div>

                <div className="job-role-card__benchmark">
                  Benchmarked as <strong>{job.benchmarkTrack}</strong> · {job.benchmarkRole}
                </div>

                <div className="job-role-card__footer">
                  <p>{job.updatedAgo}</p>
                  <div className="job-role-card__actions">
                    <button type="button" className="job-role-card__btn job-role-card__btn--outline">Apply ↗</button>
                    <button type="button" className="job-role-card__btn job-role-card__btn--primary" onClick={() => setDrawerJob(job)}>Scan my fit →</button>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>

      <ScanMyFitDrawer
        isOpen={!!drawerJob}
        job={drawerJob}
        onClose={() => setDrawerJob(null)}
        onApplySuccess={onApplySuccess}
      />
    </section>
  );
}

export default JobsOpenRolesSection;
